import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Phone, Video, Send, Paperclip, Loader2, AlertTriangle, Bot, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useConversation, useMessages } from '../hooks/useConversations';
import { PlatformIcon } from './PlatformIcon';
import { MessageBubble } from './MessageBubble';
import { DraftReviewCard } from './DraftReviewCard';
import { TypingIndicator } from './TypingIndicator';
import { EmptyMessagesState } from './EmptyStates';
import { MESSAGE_CONTAINER } from '../lib/motion';

const QUICK_REPLIES = [
  { label: 'Thank you', text: 'Thank you for contacting us!' },
  { label: 'How can I help?', text: 'How can I help you today?' },
  { label: "Checking now", text: "I'll look into this for you right away." },
  { label: 'Order status', text: 'Could you please share your order number?' },
];

export const ChatPanel = ({ conversationId }: { conversationId: string }) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { messages, sendMessage, isSending, approveDraft, discardDraft, isApproving } = useMessages(conversationId);
  const { conversation, updateConversation, isUpdating } = useConversation(conversationId);
  const isEscalated = conversation?.status === 'human';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  useEffect(() => {
    return () => clearTimeout(typingTimeoutRef.current);
  }, [conversationId]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    try {
      sendMessage({ content: message });
      setMessage('');
      clearTimeout(typingTimeoutRef.current);
      setIsTyping(true);
      typingTimeoutRef.current = setTimeout(() => setIsTyping(false), 1600);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const customerName = conversation?.customerName ?? 'Customer';
  const platform = conversation?.platform ?? 'website';
  const platformLabel = conversation
    ? conversation.platform.charAt(0).toUpperCase() + conversation.platform.slice(1)
    : '—';

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden bg-white dark:bg-slate-900">
      {/* ServerlessLens Pod Header */}
      <div className="flex items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-4 py-2.5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="flex h-8.5 w-8.5 items-center justify-center rounded-full bg-slate-200/80 dark:bg-slate-800 font-bold text-xs text-slate-700 dark:text-slate-300">
              {customerName.charAt(0).toUpperCase()}
            </div>
            <span className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-slate-100">{customerName}</h3>
              <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 px-2 py-0.2 text-[9px] font-semibold text-slate-600 dark:text-slate-300 shadow-2xs">
                <PlatformIcon platform={platform} className="h-2.5 w-2.5" />
                {platformLabel}
              </span>
            </div>
            <p className="text-[10px] text-slate-400 dark:text-slate-500">
              Session active &bull; Synced with CRM
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* AI automation badge */}
          <div className="flex items-center gap-1.5 rounded-full border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 px-2.5 py-1 shadow-2xs">
            <Sparkles className="h-3 w-3 text-indigo-600 dark:text-indigo-400" />
            <span className="text-[10px] font-bold text-slate-700 dark:text-slate-300">
              {isEscalated ? 'AI Paused' : 'AI Copilot Active'}
            </span>
          </div>

          <Button
            size="sm"
            variant="outline"
            className="h-7 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            disabled={isUpdating}
            onClick={() =>
              updateConversation({
                id: conversationId,
                status: isEscalated ? 'active' : 'human',
              })
            }
          >
            {isUpdating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : isEscalated ? (
              <>
                <Bot className="mr-1 h-3 w-3 text-emerald-600 dark:text-emerald-400" />
                Hand back to AI
              </>
            ) : (
              <>
                <AlertTriangle className="mr-1 h-3 w-3 text-amber-500 dark:text-amber-400" />
                Take Over
              </>
            )}
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="h-7 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-850 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
            onClick={() =>
              updateConversation({
                id: conversationId,
                status: 'resolved',
              })
            }
          >
            <CheckCircle2 className="mr-1 h-3 w-3 text-slate-500 dark:text-slate-400" />
            Resolve
          </Button>
        </div>
      </div>

      {/* Escalation banner */}
      {isEscalated && (
        <div className="flex items-center justify-between gap-2 border-b border-rose-200 dark:border-rose-900 bg-rose-50/80 dark:bg-rose-950/60 px-4 py-2">
          <span className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 dark:text-rose-300">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-rose-500 dark:text-rose-400" />
            Handed to human agent &mdash; AI automation is currently paused.
          </span>
          <Button
            size="sm"
            variant="outline"
            className="h-6 rounded-lg border-rose-300 dark:border-rose-800 bg-white dark:bg-slate-850 text-[11px] font-semibold text-rose-700 dark:text-rose-300 hover:bg-rose-100/50 dark:hover:bg-rose-900/50"
            disabled={isUpdating}
            onClick={() => updateConversation({ id: conversationId, status: 'active' })}
          >
            {isUpdating ? <Loader2 className="h-3 w-3 animate-spin" /> : <Bot className="mr-1 h-3 w-3 text-emerald-600 dark:text-emerald-400" />}
            Resume AI
          </Button>
        </div>
      )}

      {/* Messages Feed */}
      <div className="flex-1 overflow-y-auto bg-slate-50/40 dark:bg-slate-950/40 p-4">
        {messages.length === 0 ? (
          <EmptyMessagesState />
        ) : (
          <motion.div
            variants={MESSAGE_CONTAINER}
            initial="hidden"
            animate="show"
            className="space-y-3"
          >
            {messages.map((msg) =>
              (msg as any).status === 'draft' ? (
                <DraftReviewCard
                  key={msg.id}
                  content={msg.content}
                  onApprove={(edited) => approveDraft(msg.id, edited)}
                  onDiscard={() => discardDraft(msg.id)}
                  isApproving={isApproving}
                />
              ) : (
                <MessageBubble key={msg.id} message={msg} />
              ),
            )}
          </motion.div>
        )}
        <AnimatePresence>{isTyping && <TypingIndicator key="typing" />}</AnimatePresence>
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input Bar */}
      <div className="border-t border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 space-y-2">
        {/* Quick Replies Strip */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-0.5 scrollbar-none">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 shrink-0">
            Quick:
          </span>
          {QUICK_REPLIES.map((reply) => (
            <button
              key={reply.label}
              type="button"
              onClick={() => setMessage(reply.text)}
              className="inline-flex shrink-0 items-center rounded-full border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-850 px-2.5 py-0.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-700 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-colors shadow-2xs"
            >
              {reply.label}
            </button>
          ))}
        </div>

        <div className="flex items-end gap-2">
          <Button variant="ghost" size="sm" className="h-9 w-9 shrink-0 p-0 text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 rounded-xl">
            <Paperclip className="h-4 w-4" />
          </Button>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your reply to customer or instruct AI..."
            className="min-h-[38px] max-h-32 resize-none rounded-xl border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-850 py-2 text-xs text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:bg-white dark:focus:bg-slate-900 focus:border-primary/40 focus:ring-primary/20 transition-colors"
            rows={1}
          />
          <Button
            onClick={handleSendMessage}
            disabled={!message.trim() || isSending}
            size="sm"
            className="h-9 w-9 shrink-0 p-0 rounded-xl bg-slate-900 dark:bg-slate-100 hover:bg-slate-800 dark:hover:bg-slate-200 text-white dark:text-slate-900 shadow-xs"
          >
            {isSending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
